import { Checkbox } from '@latitude/checkbox';
import { type ChangeEvent, type ReactNode, useRef } from 'react';

type ActionListCheckboxProps = {
  isSelected: boolean;
  canSelect: boolean;
  indeterminate?: boolean;
  childrenBefore?: boolean;
  children?: ReactNode;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  className?: string;
};

/**
 * Represents a checkbox item component.
 *
 * @template TData - The type of data associated with the checkbox item.
 * @param {Object} props - The component props.
 * @param {boolean} props.isSelected - The Cell selected state.
 * @param {boolean} props.canSelect - if the cell can be selected.
 * @param {boolean} props.indeterminate - if the the checkbox is in an indeterminate state.
 * @returns {JSX.Element} The rendered checkbox item component.
 */
export const ActionListCheckbox = ({
  onChange,
  className,
  isSelected,
  canSelect,
  indeterminate,
  childrenBefore,
  children,
}: ActionListCheckboxProps) => {
  const ref = useRef<HTMLButtonElement>(null);

  const handleCheckedChange = () => {
    onChange?.({
      target: {
        checked: (ref.current as unknown as HTMLInputElement)?.checked,
      },
    } as ChangeEvent<HTMLInputElement>);
  };

  return (
    <Checkbox
      ref={ref}
      checked={isSelected}
      disabled={!canSelect}
      wrapperClassname="w-full gap-2"
      indeterminate={indeterminate}
      onCheckedChange={handleCheckedChange}
      className={className}
      childrenBefore={childrenBefore}
    >
      {children}
    </Checkbox>
  );
};
